import { createClient } from '@supabase/supabase-js';
import { DESIGN_PREVIEW } from './designPreview';

// Placeholders keep module import from throwing when config is missing.
// App.tsx shows ConfigErrorScreen instead of a blank Capacitor WebView.
const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ||
  (DESIGN_PREVIEW ? 'https://preview.invalid.supabase.co' : 'https://missing.invalid.supabase.co');
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  (DESIGN_PREVIEW ? 'preview-anon-key' : 'missing-anon-key');

/**
 * Browser Supabase client.
 *
 * `flowType: 'implicit'` is intentional: confirmation/reset emails may be
 * requested inside Capacitor but completed in Safari. PKCE needs the code
 * verifier in the same browser storage that started the flow — Capacitor
 * WKWebView storage is not Safari storage. Do not switch to PKCE without a
 * redesign around a server callback or same-browser initiation.
 *
 * `detectSessionInUrl: false` — callback routes own URL credential consumption.
 * Do not re-enable while EmailConfirmedPage / ResetPasswordPage parse the URL.
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    flowType: 'implicit',
    detectSessionInUrl: false,
    persistSession: true,
    autoRefreshToken: true,
  },
});

// Auth helper functions
export const getCurrentUser = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
};

export const getCurrentUserId = async (): Promise<string | null> => {
  const user = await getCurrentUser();
  return user?.id || null;
};

export const getAuthToken = async (): Promise<string | null> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token || null;
};

// Auth actions
export const signUp = async (email: string, password: string) => {
  return await supabase.auth.signUp({ email, password });
};

export const signIn = async (email: string, password: string) => {
  return await supabase.auth.signInWithPassword({ email, password });
};

export const signOut = async () => {
  return await supabase.auth.signOut();
};
